/**
 * COMMAND MENU - BUSCA GLOBAL
 * ============================================================================
 * Modal de busca rápida para leads, contatos e navegação
 * Atalho: CMD/CTRL + K
 *
 * Funcionalidades:
 * - Busca em tempo real (debounced)
 * - Busca por: nome, telefone, email, interesse
 * - Navegação rápida
 * - Histórico de buscas (localStorage)
 * - Atalhos de teclado
 *
 * Stack: React 18 + Radix UI Dialog + Fuse.js (fuzzy search)
 * Data: 12/11/2025
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'wouter';
import { Search, Clock, User, Phone, Mail, Home, TrendingUp, X } from 'lucide-react';
import * as Dialog from '@radix-ui/react-dialog';
import { useFirebaseAuth } from '@/contexts/FirebaseAuthContext';
import type { SearchLeadsResponse } from '@/types/api';

// ============================================================================
// TYPES
// ============================================================================

interface SearchResult {
  id: string;
  type: 'lead' | 'page';
  title: string;
  subtitle?: string;
  description?: string;
  url: string;
  icon?: React.ReactNode;
}

interface Lead {
  lead_id: string;
  nome: string;
  telefone?: string;
  email?: string;
  interesse?: string;
  status?: string;
}

// ============================================================================
// PAGES RÁPIDAS (NAVEGAÇÃO)
// ============================================================================

const QUICK_PAGES: SearchResult[] = [
  {
    id: 'home',
    type: 'page',
    title: 'Dashboard Principal',
    subtitle: 'Visão geral',
    url: '/dashboard',
    icon: <Home className='w-4 h-4' />,
  },
  {
    id: 'kanban',
    type: 'page',
    title: 'Organizador de Clientes',
    subtitle: 'Kanban visual',
    url: '/dashboard/kanban',
    icon: <TrendingUp className='w-4 h-4' />,
  },
  {
    id: 'settings',
    type: 'page',
    title: 'Configurações',
    subtitle: 'Perfil e preferências',
    url: '/dashboard/settings',
    icon: <User className='w-4 h-4' />,
  },
];

// ============================================================================
// COMPONENT
// ============================================================================

interface CommandMenuProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CommandMenu({ open, onOpenChange }: CommandMenuProps) {
  const [, setLocation] = useLocation();
  const { user } = useFirebaseAuth();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);

  // ============================================================================
  // LOAD RECENT SEARCHES
  // ============================================================================
  useEffect(() => {
    const stored = localStorage.getItem('recentSearches');
    if (stored) {
      try {
        setRecentSearches(JSON.parse(stored));
      } catch (e) {
        console.error('Erro ao carregar buscas recentes:', e);
      }
    }
  }, []);

  // ============================================================================
  // SAVE RECENT SEARCH
  // ============================================================================
  const saveRecentSearch = useCallback((searchQuery: string) => {
    if (!searchQuery.trim()) return;

    setRecentSearches(prev => {
      const updated = [searchQuery, ...prev.filter(q => q !== searchQuery)].slice(0, 5);
      localStorage.setItem('recentSearches', JSON.stringify(updated));
      return updated;
    });
  }, []);

  // ============================================================================
  // FETCH LEADS FROM API
  // ============================================================================
  const fetchLeads = useCallback(
    async (searchQuery: string): Promise<SearchResult[]> => {
      if (!searchQuery.trim() || !user?.uid) return [];

      try {
        const response = await fetch(
          `/api/leads?userId=${user.uid}&search=${encodeURIComponent(searchQuery)}&limit=8`
        );

        if (!response.ok) {
          throw new Error('Erro ao buscar leads');
        }

        const data = (await response.json()) as SearchLeadsResponse;
        const leads: Lead[] = data.leads || [];

        return leads.map(lead => ({
          id: lead.lead_id,
          type: 'lead' as const,
          title: lead.nome || 'Sem nome',
          subtitle: lead.telefone || lead.email || '',
          description: lead.interesse || lead.status || '',
          url: `/dashboard/organizador-clientes/leads/${lead.lead_id}`,
          icon: <User className='w-4 h-4' />,
        }));
      } catch (error) {
        console.error('Erro ao buscar leads:', error);
        return [];
      }
    },
    [user?.uid]
  );

  // ============================================================================
  // SEARCH (DEBOUNCED)
  // ============================================================================
  const performSearch = useCallback(
    async (searchQuery: string) => {
      if (!searchQuery.trim()) {
        setResults([]);
        setLoading(false);
        return;
      }

      setLoading(true);

      // Buscar em páginas
      const pageResults = QUICK_PAGES.filter(
        page =>
          page.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          page.subtitle?.toLowerCase().includes(searchQuery.toLowerCase())
      );

      // Buscar em leads
      const leadResults = await fetchLeads(searchQuery);

      // Combinar resultados (leads primeiro, depois páginas)
      setResults([...leadResults, ...pageResults]);
      setLoading(false);
      setSelectedIndex(0);
    },
    [fetchLeads]
  );

  // ============================================================================
  // DEBOUNCED SEARCH
  // ============================================================================
  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      performSearch(query);
    }, 300);

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [query, performSearch]);

  // ============================================================================
  // KEYBOARD NAVIGATION
  // ============================================================================
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!open) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(prev => (prev + 1) % results.length);
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(prev => (prev - 1 + results.length) % results.length);
          break;
        case 'Enter':
          e.preventDefault();
          if (results[selectedIndex]) {
            handleSelect(results[selectedIndex]);
          }
          break;
        case 'Escape':
          e.preventDefault();
          onOpenChange(false);
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, results, selectedIndex, onOpenChange]);

  // ============================================================================
  // GLOBAL SHORTCUT (CMD/CTRL + K)
  // ============================================================================
  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        onOpenChange(true);
      }
    };

    window.addEventListener('keydown', handleGlobalKeyDown);
    return () => window.removeEventListener('keydown', handleGlobalKeyDown);
  }, [onOpenChange]);

  // ============================================================================
  // FOCUS INPUT ON OPEN
  // ============================================================================
  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  // ============================================================================
  // HANDLE SELECT
  // ============================================================================
  const handleSelect = (result: SearchResult) => {
    saveRecentSearch(query);
    setLocation(result.url);
    onOpenChange(false);
    setQuery('');
    setResults([]);
  };

  // ============================================================================
  // CLEAR SEARCH
  // ============================================================================
  const handleClear = () => {
    setQuery('');
    setResults([]);
    setSelectedIndex(0);
    inputRef.current?.focus();
  };

  // ============================================================================
  // RENDER
  // ============================================================================
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        {/* Overlay */}
        <Dialog.Overlay
          className='fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm
                     data-[state=open]:animate-fadeIn'
        />

        {/* Content */}
        <Dialog.Content
          className='fixed top-[20vh] left-1/2 -translate-x-1/2 z-[101]
                     w-[90vw] max-w-2xl
                     bg-white dark:bg-gray-900
                     border border-gray-200 dark:border-gray-800
                     rounded-brand-lg
                     shadow-brand-xl
                     data-[state=open]:animate-slideDown
                     overflow-hidden'
        >
          {/* Header - Search Input */}
          <div className='flex items-center gap-3 px-4 py-3 border-b border-gray-200 dark:border-gray-800'>
            <Search className='w-5 h-5 text-gray-400 dark:text-gray-500 flex-shrink-0' />
            <input
              ref={inputRef}
              type='text'
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder='Buscar leads, contatos, páginas...'
              className='flex-1 bg-transparent border-none outline-none
                         text-gray-900 dark:text-white
                         placeholder:text-gray-400 dark:placeholder:text-gray-500
                         text-base'
              autoComplete='off'
              autoCorrect='off'
              autoCapitalize='off'
              spellCheck='false'
            />
            {query && (
              <button
                onClick={handleClear}
                className='p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800
                           text-gray-400 hover:text-gray-600 dark:hover:text-gray-300
                           transition-colors'
                aria-label='Limpar busca'
              >
                <X className='w-4 h-4' />
              </button>
            )}
            <kbd
              className='hidden sm:inline-flex items-center gap-1 px-2 py-1
                            bg-gray-100 dark:bg-gray-800
                            border border-gray-300 dark:border-gray-600
                            rounded text-xs font-mono text-gray-600 dark:text-gray-400'
            >
              ESC
            </kbd>
          </div>

          {/* Results */}
          <div className='max-h-[60vh] overflow-y-auto p-2'>
            {loading && (
              <div className='flex items-center justify-center py-8'>
                <div className='animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500' />
              </div>
            )}

            {!loading && results.length === 0 && query && (
              <div className='flex flex-col items-center justify-center py-12 text-center'>
                <Search className='w-12 h-12 text-gray-300 dark:text-gray-700 mb-3' />
                <p className='text-sm text-gray-500 dark:text-gray-400'>
                  Nenhum resultado encontrado para "<span className='font-semibold'>{query}</span>"
                </p>
              </div>
            )}

            {!loading && results.length === 0 && !query && recentSearches.length > 0 && (
              <div className='py-2'>
                <p className='px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide'>
                  Buscas recentes
                </p>
                {recentSearches.map((search, index) => (
                  <button
                    key={index}
                    onClick={() => setQuery(search)}
                    className='flex items-center gap-3 w-full px-3 py-2 rounded-md
                               text-sm text-gray-700 dark:text-gray-300
                               hover:bg-gray-100 dark:hover:bg-gray-800
                               transition-colors'
                  >
                    <Clock className='w-4 h-4 text-gray-400' />
                    <span>{search}</span>
                  </button>
                ))}
              </div>
            )}

            {!loading && results.length > 0 && (
              <div className='py-2'>
                {results.map((result, index) => (
                  <button
                    key={result.id}
                    onClick={() => handleSelect(result)}
                    onMouseEnter={() => setSelectedIndex(index)}
                    className={`flex items-start gap-3 w-full px-3 py-2.5 rounded-md
                               text-left transition-colors
                               ${
                                 index === selectedIndex
                                   ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                                   : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                               }`}
                  >
                    <div className='flex-shrink-0 mt-0.5'>{result.icon}</div>
                    <div className='flex-1 min-w-0'>
                      <p className='font-medium truncate'>{result.title}</p>
                      {result.subtitle && (
                        <p className='text-sm text-gray-500 dark:text-gray-400 truncate'>
                          {result.subtitle}
                        </p>
                      )}
                      {result.description && (
                        <p className='text-xs text-gray-400 dark:text-gray-500 truncate mt-0.5'>
                          {result.description}
                        </p>
                      )}
                    </div>
                    <div className='flex-shrink-0 flex items-center gap-2'>
                      {result.type === 'lead' && (
                        <span className='text-xs bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 px-2 py-0.5 rounded'>
                          Lead
                        </span>
                      )}
                      {result.type === 'page' && (
                        <span className='text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 px-2 py-0.5 rounded'>
                          Página
                        </span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}

            {!loading && !query && recentSearches.length === 0 && (
              <div className='py-2'>
                <p className='px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide'>
                  Páginas rápidas
                </p>
                {QUICK_PAGES.map((page, index) => (
                  <button
                    key={page.id}
                    onClick={() => handleSelect(page)}
                    className='flex items-center gap-3 w-full px-3 py-2 rounded-md
                               text-sm text-gray-700 dark:text-gray-300
                               hover:bg-gray-100 dark:hover:bg-gray-800
                               transition-colors'
                  >
                    {page.icon}
                    <div className='flex-1 text-left'>
                      <p className='font-medium'>{page.title}</p>
                      {page.subtitle && (
                        <p className='text-xs text-gray-500 dark:text-gray-400'>{page.subtitle}</p>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Footer - Shortcuts */}
          <div
            className='flex items-center justify-between px-4 py-2 border-t border-gray-200 dark:border-gray-800
                          bg-gray-50 dark:bg-gray-900/50'
          >
            <div className='flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400'>
              <div className='flex items-center gap-1.5'>
                <kbd className='px-1.5 py-0.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded font-mono'>
                  ↑↓
                </kbd>
                <span>Navegar</span>
              </div>
              <div className='flex items-center gap-1.5'>
                <kbd className='px-1.5 py-0.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded font-mono'>
                  ↵
                </kbd>
                <span>Selecionar</span>
              </div>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export default CommandMenu;
